/**
 * Market Slide
 * SAM/SOM/TAM table with conditional highlights for changed values
 * and blue-to-green heatmap on ARR cells.
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing, borderRadius } from '@config/spacing';
import { SlideBase, SlideHeader, SeeMoreLink } from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Layout
// ============================================



// ============================================
// Table
// ============================================

const TableArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const TableContainer = styled.div`
  width: 100%;
  max-width: 960px;
`;

/* 9 columns: Tier | # HH | Dual Income | Kids | Income | Location | ARR@5% | ARR@10% | ARR@50% */
const GRID = '1.3fr 0.9fr 0.9fr 0.6fr 0.8fr 0.8fr 1fr 1fr 1fr';

// ── Group header row ──

const GroupRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID};
  gap: 2px;
  padding: 0 ${spacing.sm};
  margin-bottom: 2px;
`;

const GroupLabel = styled.div<{ $span: string }>`
  grid-column: ${(p) => p.$span};
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 1vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
  text-transform: uppercase;
  letter-spacing: 1px;
  padding-bottom: 4px;
  border-bottom: 1px solid ${colors.neutral.light};
`;

// ── Column header row ──

const ColHeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID};
  gap: 2px;
  padding: 6px ${spacing.sm};
  border-bottom: 2px solid ${colors.brand.primaryDark};
`;

const ColHeader = styled.div<{ $align?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.xxs}, 0.9vw, ${typography.fontSize.xs});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.light};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: ${(p) => p.$align || 'center'};
`;

// ── Data rows ──

const DataRow = styled.div`
  display: grid;
  grid-template-columns: ${GRID};
  gap: 2px;
  padding: 10px ${spacing.sm};
  align-items: center;
  border-bottom: 1px solid ${colors.neutral.extraLight};

  &:last-child {
    border-bottom: none;
  }
`;

const CellTier = styled.div`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.base}, 2.2vw, ${typography.fontSize.lg});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
`;

const CellText = styled.div<{ $align?: string }>`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 2vw, ${typography.fontSize.lg});
  color: ${colors.highlight.primary};
  text-align: ${(p) => p.$align || 'center'};
`;

const CellDim = styled(CellText)`
  color: ${colors.highlight.extraLight};
`;

/** Highlighted cell: the value that changed from the previous tier */
const CellHighlight = styled(CellText)`
  color: ${colors.highlight.extraDark};
  font-weight: ${typography.fontWeight.bold};
  background: ${colors.brand.primary}60;
  border-radius: ${borderRadius.sm};
  padding: 2px 8px;
  margin: -2px -8px;
  display: inline-block;
`;

const CellHH = styled.div<{ $align?: string }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.base}, 2.2vw, ${typography.fontSize.lg});
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.highlight.dark};
  text-align: ${(p) => p.$align || 'right'};
`;

/** ARR cell with heatmap background */
const CellARR = styled.div<{ $bg: string; $align?: string; $light?: boolean }>`
  font-family: ${typography.fontFamily.heading};
  font-size: clamp(${typography.fontSize.sm}, 2vw, ${typography.fontSize.lg});
  font-weight: ${typography.fontWeight.bold};
  color: ${(p) => p.$light ? '#fff' : colors.highlight.extraDark};
  text-align: ${(p) => p.$align || 'right'};
  background: ${(p) => p.$bg};
  border-radius: ${borderRadius.sm};
  padding: 4px 8px;
`;

const AlertBanner = styled.div`
  width: 100%;
  max-width: 960px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-left: 4px solid ${colors.accent.teal.exxtraDark};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.md} ${spacing.xl};
  font-family: ${typography.fontFamily.body};
  font-size: clamp(${typography.fontSize.sm}, 1.4vw, ${typography.fontSize.base});
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.highlight.dark};
  line-height: ${typography.lineHeight.relaxed};
`;

// ============================================
// Heatmap: teal gradient (light → dark)
// 18 cells (6 tiers × 3 ARR columns)
// ============================================

const T = colors.accent.teal;

const HEATMAP = [
  // Row 0 - Beachhead ($3M / $6M / $32M)
  { arr5: T.light,      arr10: T.light,      arr50: T.base },
  // Row 1 - Adjacent ($22M / $43M / $215M)
  { arr5: T.light,      arr10: T.base,       arr50: T.dark },
  // Row 2 - SOM ($118M / $237M / $1B)
  { arr5: T.dark,       arr10: T.dark,       arr50: T.extraDark },
  // Row 3 - SAM ($172M / $345M / $2B)
  { arr5: T.dark,       arr10: T.extraDark,  arr50: T.exxtraDark },
  // Row 4 - TAM ($592M / $1B / $6B)
  { arr5: T.extraDark,  arr10: T.exxtraDark, arr50: T.exxxtraDark },
  // Row 5 - TAM* ($1B / $3B / $14B)
  { arr5: T.exxtraDark, arr10: T.exxxtraDark, arr50: T.exxxtraDark },
];

/** White text on dark teal backgrounds */
const DARK_BG: Set<string> = new Set([T.extraDark, T.exxtraDark, T.exxxtraDark]);
const isDarkBg = (bg: string) => DARK_BG.has(bg);

// ============================================
// Data
// ============================================

interface Tier {
  name: string;
  dualIncome: string;
  kids: string;
  location: string;
  income: string;
  hh: string;
  arr5: string;
  arr10: string;
  arr50: string;
  /** Which characteristic column changed vs previous tier */
  changed: 'none' | 'location' | 'income' | 'dualIncome' | 'kids';
}

const TIERS: Tier[] = [
  {
    name: 'Beachhead',
    dualIncome: 'Yes',
    kids: 'Yes',
    location: 'CA',
    income: '$250K+',
    hh: '0.3M',
    arr5: '$3M',
    arr10: '$6M',
    arr50: '$32M',
    changed: 'none',
  },
  {
    name: 'Adjacent',
    dualIncome: 'Yes',
    kids: 'Yes',
    location: 'US',
    income: '$250K+',
    hh: '2M',
    arr5: '$22M',
    arr10: '$43M',
    arr50: '$215M',
    changed: 'location',
  },
  {
    name: 'SOM',
    dualIncome: 'Yes',
    kids: 'Yes',
    location: 'US',
    income: '$100K+',
    hh: '11M',
    arr5: '$118M',
    arr10: '$237M',
    arr50: '$1B',
    changed: 'income',
  },
  {
    name: 'SAM',
    dualIncome: '\u2014',
    kids: 'Yes',
    location: 'US',
    income: '$100K+',
    hh: '16M',
    arr5: '$172M',
    arr10: '$345M',
    arr50: '$2B',
    changed: 'dualIncome',
  },
  {
    name: 'TAM',
    dualIncome: '\u2014',
    kids: '\u2014',
    location: 'US',
    income: '$100K+',
    hh: '55M',
    arr5: '$592M',
    arr10: '$1B',
    arr50: '$6B',
    changed: 'kids',
  },
  {
    name: 'TAM*',
    dualIncome: '\u2014',
    kids: '\u2014',
    location: 'US',
    income: '\u2014',
    hh: '130M',
    arr5: '$1B',
    arr10: '$3B',
    arr50: '$14B',
    changed: 'income',
  },
];

// ============================================
// Helpers
// ============================================

/** Render a characteristic cell, highlighting if it's the one that changed */
const CharCell: React.FC<{
  value: string;
  field: string;
  changed: string;
  isDash?: boolean;
}> = ({ value, field, changed, isDash }) => {
  if (field === changed) {
    return <CellHighlight>{value}</CellHighlight>;
  }
  if (isDash) {
    return <CellDim>{value}</CellDim>;
  }
  return <CellText>{value}</CellText>;
};

// ============================================
// Component
// ============================================

export const MarketSlide: React.FC = () => (
  <SlideBase>
    <SlideHeader title="Market" subtitle="Subscription Business Model @ $17.95/month" />

    <TableArea>
      <TableContainer>
        {/* Group headers */}
        <GroupRow>
          <GroupLabel $span="1 / 3">Market Segment</GroupLabel>
          <GroupLabel $span="3 / 7"></GroupLabel>
          <GroupLabel $span="7 / 10" style={{ textAlign: 'center' }}>
            ARR by Market Share
          </GroupLabel>
        </GroupRow>

        {/* Column headers */}
        <ColHeaderRow>
          <ColHeader $align="left" />
          <ColHeader $align="right">Households</ColHeader>
          <ColHeader>Dual<br />Income</ColHeader>
          <ColHeader>Kids</ColHeader>
          <ColHeader>Income</ColHeader>
          <ColHeader>Location</ColHeader>
          <ColHeader $align="right">ARR @5%</ColHeader>
          <ColHeader $align="right">ARR @10%</ColHeader>
          <ColHeader $align="right">ARR @50%</ColHeader>
        </ColHeaderRow>

        {/* Data rows */}
        {TIERS.map((tier, i) => (
          <DataRow key={tier.name}>
            <CellTier>{tier.name}</CellTier>
            <CellHH $align="right">{tier.hh}</CellHH>
            <CharCell
              value={tier.dualIncome}
              field="dualIncome"
              changed={tier.changed}
              isDash={tier.dualIncome === '\u2014'}
            />
            <CharCell
              value={tier.kids}
              field="kids"
              changed={tier.changed}
              isDash={tier.kids === '\u2014'}
            />
            <CharCell
              value={tier.income}
              field="income"
              changed={tier.changed}
              isDash={tier.income === '\u2014'}
            />
            <CharCell
              value={tier.location}
              field="location"
              changed={tier.changed}
            />
            <CellARR $bg={HEATMAP[i].arr5} $align="right" $light={isDarkBg(HEATMAP[i].arr5)}>
              {tier.arr5}
            </CellARR>
            <CellARR $bg={HEATMAP[i].arr10} $align="right" $light={isDarkBg(HEATMAP[i].arr10)}>
              {tier.arr10}
            </CellARR>
            <CellARR $bg={HEATMAP[i].arr50} $align="right" $light={isDarkBg(HEATMAP[i].arr50)}>
              {tier.arr50}
            </CellARR>
          </DataRow>
        ))}
      </TableContainer>
    </TableArea>
    
    <AlertBanner>
      Competitive Differentiators: Trust, Community Engagement, Household Context, and Use Case Coverage
    </AlertBanner>

    <SeeMoreLink>See Methodology</SeeMoreLink>
  </SlideBase>
);
